/**
 * Step-through wizard for customer profile selection.
 * Handles multi-step form navigation, answer collection, and API submission.
 * 
 * @description This module provides:
 * - Opening wizard for unauthenticated customers
 * - Step-by-step navigation with conditional visibility
 * - Answer validation and collection
 * - Submitting results to API
 * 
 * @requires getCookie, setCookie (global functions)
 * 
 * @example
 * // Automatically initializes on page load
 * stepThroughWizard.init();
 */
let stepThroughWizard = {
  /** @type {HTMLElement|null} Wizard container element */
  wizardEl: null,
  /** @type {boolean} Tracks if wizard is open */
  wizardOpen: false,
  /** @type {boolean} Tracks if wizard is finished */
  finished: false,
  /** @type {number} Current step index */
  currentStep: 0,
  /** @type {Array} Step configuration from data attribute */
  steps: null,
  /** @type {Array} Collected answers from all steps */
  answers: [],

  /**
   * Initializes the wizard functionality.
   * Checks if wizard should open based on login state and completion cookie.
   * 
   * @returns {void}
   * @sideEffect Sets wizard properties, may open wizard, binds events
   */
  init: function () {
    let _this = this;
    // Get completion state from window
    this.finished = window.customerProfileFinished;
    this.loggedIn = window.customerLoggedIn;
    this.wizardEl = document.querySelector('.step-through-wizard');
    this.resetWizard = document.querySelector('[data-button-reset-wizard]');

    // Parse steps from data attribute
    if (this.wizardEl) {
      this.steps = JSON.parse(this.wizardEl.dataset.steps);
    }

    // Open wizard for logged-in customers who haven't finished
    if (this.loggedIn && !this.finished && !getCookie('step-through-wizard-completed')) {
      this.openWizard();
    }

    this.bindEvents();
  },

  /**
   * Binds event listeners for wizard interactions.
   * Handles select changes, submit button, and reset button.
   * 
   * @returns {void}
   * @sideEffect Adds event listeners to wizard elements
   */
  bindEvents: function () {
    let _this = this;

    // Handle step selection changes
    this.wizardEl.querySelectorAll('.step .select-container select').forEach(el => {
      el.addEventListener('change', e => {
        let currentStep = parseInt(el.getAttribute('name').split('-')[1]);
        _this.checkSteps(currentStep);
      });
    });

    // Handle submit button
    this.wizardEl.querySelector('.submit-profile-btn').addEventListener('click', e => {
      _this.submitWizard();
    });

    // Handle reset button (with confirmation)
    if (this.resetWizard) {
      this.resetWizard.addEventListener('click', e => {
        let text = this.resetWizard.dataset.resetWizardText;
        if (confirm(text)) {
          this.openWizard();
        }
      });
    }
  },

  /**
   * Validates and updates wizard step visibility based on current selection.
   * Handles conditional step visibility, early completion, and scroll positioning.
   * 
   * @param {number} currentStep - The step index that was changed
   * @returns {void}
   * @sideEffect Updates step visibility, answer array, may auto-submit
   */
  checkSteps: function (currentStep) {
    if (!this.wizardEl || !this.steps) return;

    this.answers = [];
    let _this = this;
    let steps = this.wizardEl.querySelectorAll('.step .select-container select');

    this.currentStep = currentStep;

    // Reset subsequent selections if going back
    if (this.currentStep + 1 <= steps.length) {
      steps.forEach((el, index) => {
        if (index == this.currentStep + 1) {
          el.querySelectorAll('option').forEach((opt, _index) => {
            if (_index < 1) {
              opt.selected = true;
              opt.dispatchEvent(new Event('click'));
            } else {
              opt.selected = false;
            }
          });
        } else if (index > this.currentStep + 1) {
          // Hide future steps
          el.closest('.step').classList.add('hidden');
        }
      });
    }

    // Collect valid answers
    let shouldSkip = false;
    let i = 0;
    steps.forEach((el, index, arr) => {
      if (shouldSkip) {
        i++;
        return;
      }

      // Skip empty or future steps
      if (el.value.length < 1 || parseInt(el.id.replace('step-', '')) > _this.currentStep) {
        shouldSkip = true;
        i++;
        return;
      }
      // Store answer with step index
      _this.answers[i] = (el.selectedIndex - 1) + '-' + el.value;
      i++;
    });

    // Check for early completion option
    steps.forEach((el, index) => {
      el.querySelectorAll('option').forEach((opt) => {
        if (opt.selected) {
          let endsEarly = opt.dataset.endsEarly === "true";
          if (endsEarly) {
            _this.successLoader();
            _this.submitWizard();
            document.body.classList.add('select-block');
            return;
          }
        }
      });
    });

    // Show/hide next step options based on conditions
    let nextStep = this.currentStep + 1;
    let nextEl = this.wizardEl.querySelector('.step-' + nextStep);
    if (nextEl) {
      nextEl.querySelectorAll('option').forEach(option => {
        // Parse conditions from data attribute
        let conditions = JSON.parse(option.dataset.conditions);
        let action = option.dataset.action === 'show';
        let conditionMet = null;
        
        if (conditions) {
          conditions.forEach(condition => {
            if (condition.comparison == 'is') {
              let step = parseInt(condition.step);
              let targetAnswer = _this.answers[step];
              let _targetAnswer = targetAnswer.split('-');
              let targetValue = _targetAnswer[1];

              if (targetValue == condition.answer) {
                conditionMet = true;
              }
              else if(conditionMet == null) conditionMet = false;
            } else if (condition.comparison == 'is not') {
              let step = parseInt(condition.step);
              let targetAnswer = _this.answers[step];
              let _targetAnswer = targetAnswer.split('-');
              let targetValue = _targetAnswer[1];

              if (targetValue == condition.answer) {
                conditionMet = action;
              }
            }
          });
        }

        // Show/hide option based on conditions
        if (!conditions) {
          option.classList.remove('hidden');
        } else if (conditionMet) {
          if (action) option.classList.remove('hidden');
          else option.classList.add('hidden');
        } else {
          if (action) option.classList.add('hidden');
          else option.classList.remove('hidden');
        }
      });

      this.wizardEl.querySelector('.step-' + nextStep).classList.remove('hidden');
    }

    // Show/hide footer (submit button) based on final step completion
    if (this.wizardEl.querySelector('.step-' + (steps.length - 1)) != null && 
        this.wizardEl.querySelector('.step-' + (steps.length - 1) + ' select').value.length > 0) {
      if (this.wizardEl.querySelector('.footer') != null) {
        this.wizardEl.querySelector('.footer').classList.remove('hidden');
      }
      this.wizardEl.classList.add('finished');
    } else {
      this.wizardEl.querySelector('.footer').classList.add('hidden');
      this.wizardEl.classList.remove('finished');
    }

    // Scroll to bottom after updates
    this.scrollToBottom();
  },

  /**
   * Scrolls the wizard container to the bottom.
   * Used to ensure latest content is visible.
   * 
   * @returns {void}
   */
  scrollToBottom: function () {
    if (this.wizardEl == null) return;
    this.wizardEl.scrollTo(0, this.wizardEl.scrollHeight);
  },

  /**
   * Opens the wizard by removing hidden class and setting body state.
   * 
   * @returns {void}
   * @sideEffect Modifies CSS classes on wizard and body
   */
  openWizard: function () {
    if (this.wizardEl == null) return;
    this.wizardEl.classList.remove('hidden');
    this.wizardOpen = true;
    document.querySelector('body').classList.add('wizard-open');
  },

  /**
   * Submits wizard answers to the API and redirects on success.
   * Collects all answers, formats them, and sends to backend.
   * 
   * @returns {void}
   * @sideEffect Makes API call, sets cookie, redirects page
   */
  submitWizard: function () {
    if (this.wizardEl == null) return;
    
    // Get configuration from DOM
    let domain = document.querySelector('body').dataset.domain;
    let allResults = JSON.parse(this.wizardEl.dataset.results);
    let result = null;

    // Format answers to just step IDs
    let newAnswers = [];
    this.answers.forEach(answer => {
      let id = answer.split('-')[0];
      newAnswers.push(id);
    });

    let answers = '_' + newAnswers.join(',');

    // Get customer and store IDs
    let customerId = this.wizardEl.dataset.customerId;
    let oldResults = this.wizardEl.dataset.customerResults;
    let storeId = this.wizardEl.dataset.storeId;

    // Determine result based on answers
    if (Array.isArray(allResults)) {
      if (!isNaN(parseInt(answers)) && (parseInt(answers) - 1) <= allResults.length) {
        result = allResults[parseInt(answers)];
      }
    } else if (allResults.hasOwnProperty(answers)) {
      result = allResults[answers];
    }

    // Build results array (supporting multiple submissions)
    let results = [];
    if (oldResults && typeof oldResults == 'string' && oldResults.substring(0, 1) == '[') {
      results = JSON.parse(oldResults);
    }

    results.push({
      id: storeId,
      result: result,
      answers: answers, // Stored for potential reset if wizard setup changes
    });

    // Prepare API payload
    let data = {
      customer_id: customerId,
      results: JSON.stringify(results),
    };

    // Submit to API
    fetch(window.api_base_url + '/api/customer/store-wizard-completion', {
      'method': 'POST',
      'headers': {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    }).then(response => {
      // Remember completion for 1 day
      setCookie('step-through-wizard-completed', 'true', null, 1);
      // Redirect to success page
      window.location.href = "/pages/" + this.wizardEl.dataset.successPage;
    });
  },

  /**
   * Shows loading overlay while wizard is being submitted.
   * 
   * @returns {void}
   * @sideEffect Removes hidden class from overlay and loader
   */
  successLoader: function () {
    const overlay = document.querySelector('[data-wizard-overlay]');
    const loader = document.querySelector('[data-wizard-loader]');

    overlay.classList.remove('hidden');
    loader.classList.remove('hidden');
  }
}

// Initialize on page load
stepThroughWizard.init();
